import React from "react";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import {
  useEditEvent,
  useRetrieveCurrentPlanzone,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import { useModal } from "@root/client/modal";
import { EditIcon } from "@common/editIcon";
import { MagnifyingGlassIcon } from "@common/magnifyingGlassIcon";
import { TrashIcon } from "@common/trashIcon";
import { Safeguard } from "@scenes/safeguard";
import { RichTextInput } from "@common/richTextInput";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { ProjectElementsSecondaryCriteria } from "@scenes/projectElementsSecondaryCriteria";
import { Props } from "./types";
import styles from "./styles.scss";
import { ScrollingText } from "@common/scrollingText";
import { FolderIcon } from "@common/folderIcon";
import { Link } from "react-router-dom";

function EventPopup({ event, project }: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { isRequestPending: isEditRequestPending, editEvent } =
    useEditEvent(event);
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "projectElement",
  );
  const {
    template: projectTemplate,
    isRequestSuccess: isProjectTemplateSuccess,
    isRequestPending: isProjectTemplatePending,
    isRequestFailure: isProjectTemplateFailure,
    retrieveSecondaryCriteriaTemplate,
  } = useRetrieveSecondaryCriteriaTemplate(project.uuid, "projectElement");

  React.useEffect(() => {
    !!project.uuid &&
      !isProjectTemplatePending &&
      !isProjectTemplateSuccess &&
      !isProjectTemplateFailure &&
      retrieveSecondaryCriteriaTemplate({
        namespace: project.uuid,
        scope: "project",
        elementType: "projectElement",
      });
  }, [project.uuid]);

  const template = [...planzoneTemplate, ...projectTemplate];

  const formatDateTime = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString("fr-FR", {
      weekday: "short",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatTime = (date: string) => {
    return new Date(date).toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Safeguard useCases={[UseCases.editEvent, UseCases.removeEvent]}>
      {({ canEditEvent, canRemoveEvent }) => (
        <div className={styles.eventPopup}>
          <div className={styles.header}>
            <div className={styles.dateTimeInfo}>
              <span className={styles.timeRange}>
                {formatTime(event.startTime)} - {formatTime(event.endTime)}
              </span>
              <span className={styles.date}>
                {formatDateTime(event.startTime)}
              </span>
            </div>
            <div className={styles.actions}>
              <button
                className={styles.actionButton}
                onClick={() => {
                  pushView({
                    data: { event, isMultiProject: true },
                    key: UseCases.retrieveEvent,
                  });
                }}
                title={t("eventPopup.edit")}
              >
                {canEditEvent ? (
                  <EditIcon className={styles.editIcon} />
                ) : (
                  <MagnifyingGlassIcon className={styles.editIcon} />
                )}
              </button>
              {canRemoveEvent && (
                <button
                  className={styles.actionButton}
                  onClick={() => {
                    pushView({
                      data: event,
                      key: UseCases.removeEvent,
                    });
                  }}
                  title={t("eventPopup.delete")}
                >
                  <TrashIcon className={styles.trashIcon} />
                </button>
              )}
            </div>
          </div>

          <div className={styles.title}>
            <ScrollingText speed={100}>
              <h3 className={styles.name}>{event.name}</h3>
            </ScrollingText>
          </div>

          {event.description && (
            <RichTextInput
              value={event.description}
              isDisabled
              input={() => {}}
              className={styles.description}
            />
          )}

          <div className={styles.projectInfo}>
            <span className={styles.label}>{t("eventPopup.project")}:</span>
            <div className={styles.projectBadge}>
              <FolderIcon className={styles.icon} />
              <Link to={`/projects/${project.uuid}`} className={styles.link}>
                <span className={styles.projectName}>{project.name}</span>
              </Link>
            </div>
          </div>

          {template.length > 0 && (
            <div className={styles.customFields}>
              <ProjectElementsSecondaryCriteria
                project={project}
                criteriaForms={template.reduce(
                  (acc, criteria) => ({
                    ...acc,
                    [criteria.uuid]: (
                      <SecondaryCriteriaForm
                        criteria={criteria}
                        value={
                          event.attributes
                            ? event.attributes[criteria.uuid] || ""
                            : ""
                        }
                        disabled={isEditRequestPending}
                        submit={(value) =>
                          editEvent({
                            event,
                            nextEvent: {
                              ...event,
                              attributes: {
                                ...event.attributes,
                                [criteria.uuid]: value,
                              },
                            },
                          })
                        }
                      />
                    ),
                  }),
                  {},
                )}
              />
            </div>
          )}
        </div>
      )}
    </Safeguard>
  );
}

export { EventPopup };
